import {
  AbstractControl,
  AbstractControlOptions,
  AsyncValidatorFn,
  FormControlOptions,
  FormControlState,
  isFormArray,
  isFormControl,
  isFormGroup,
  ValidatorFn,
  Validators
} from '@angular/forms';
import {DestroyRef, inject, QueryList} from '@angular/core';
import {asArray, hasNoValue, hasValue, isArray, isDate, isDefined, isFunction, isObject, isString, isUndefined} from '@aviary/store/common/typing.helpers';
import {OneOrArrayOf} from '@aviary/store/utility-type.helpers';
import {ExtendedFormArray} from '@aviary/forms/extensions/extended-form-array/extended-form-array';
import {ExtendedFormGroup} from '@aviary/forms/extensions/extended-form-group/extended-form-group';
import {ExtendedFormControl} from '@aviary/forms/extensions/extended-form-control/extended-form-control';
import deepEqual from 'fast-deep-equal';
import {ControlRef, ControlsFor, ExtendedFormGroupArray, FormRowRef} from './fields/form-fields.types';
import {ExtendedFormBuilder} from '@aviary/forms/extensions/extended-form-builder/extended-form-builder';
import {
  ExtendedAbstractControl,
  ExtendedAsyncValidatorFn,
  isExtendedFormControlOptions,
  ObjectValidators,
  ValidatorOptions
} from '@aviary/forms/extensions/form-extensions.types';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

/**
 * Combines a list of initial values and validators into a standard non-nullable ExtendedFormGroup.
 *
 * Note that the `formBuilder` argument is not normally needed because it is automatically injected.  However, if this is called from outside an object
 * construction phase, then formBuilder can be injected separately and passed in. (For example, from inside a class method.)
 */
export function buildFormGroup<ObjectType extends object>(
  initialValues: ObjectType,
  validators: ObjectValidators<ObjectType>,
  formBuilder?: ExtendedFormBuilder,
  options?: AbstractControlOptions | null
): ExtendedFormGroup<ControlsFor<ObjectType>> {
  const _formBuilder: ExtendedFormBuilder = formBuilder ?? inject(ExtendedFormBuilder);

  return _formBuilder.nonNullable.extendedGroup<ControlsFor<ObjectType>>(
    Object.entries(initialValues).reduce<ControlsFor<ObjectType>>((accumulator, [propertyName, initialValue]) => {
      if (isNestedObjectValidators(validators[propertyName])) {
        // If this property itself has nested validators, recursively build a nested form group.
        accumulator[propertyName as keyof ControlsFor<ObjectType>] = buildFormGroup(initialValue, validators[propertyName], _formBuilder);
      } else {
        const [syncValidators, asyncValidators, updateOn]: ValidatorOptions = getValidatorOptionsForKey(validators, propertyName as keyof ObjectType);

        accumulator[propertyName as keyof ControlsFor<ObjectType>] =
          _formBuilder.nonNullable.extendedControl(
            initialValue,
            {validators: syncValidators, asyncValidators, updateOn}
          );
      }

      return accumulator;
    }, {} as ControlsFor<ObjectType>),
    options ?? undefined
  );
}

function isNestedObjectValidators(thing: unknown): thing is ObjectValidators<object> {
  return isObject(thing) && typeof thing === 'object' && !isFormControlOptions(thing);
}

export function isFormControlOptions(thing: unknown): thing is FormControlOptions {
  return isObject(thing) && ('validators' in thing ||
    'asyncValidators' in thing ||
    'updateOn' in thing ||
    'nonNullable' in thing ||
    'initialValueIsDefault' in thing);
}


function getValidatorOptionsForKey<T extends object>(validators: ObjectValidators<T>, key: keyof T): ValidatorOptions {
  const validatorEntry: ObjectValidators<T>[keyof T] = validators[key];

  if (isUndefined(validatorEntry)) return [undefined, undefined, undefined];
  if (isFunction(validatorEntry)) return [[validatorEntry], undefined, undefined];
  if (isArray(validatorEntry)) return [validatorEntry, undefined, undefined];

  return isExtendedFormControlOptions(validatorEntry)
    ? [
      normalizeValidators(validatorEntry.validators),
      normalizeAsyncValidator(validatorEntry.asyncValidators),
      validatorEntry.updateOn
    ]
    : [undefined, undefined, undefined];
}

function normalizeValidators(validators?: OneOrArrayOf<ValidatorFn> | null): ValidatorFn[] | undefined {
  if (!validators) return undefined;

  return asArray(validators);
}

function normalizeAsyncValidator(validators?: OneOrArrayOf<ExtendedAsyncValidatorFn> | null): AsyncValidatorFn | undefined {
  if (!validators) return undefined;

  const normalized: AsyncValidatorFn[] = asArray(validators) as AsyncValidatorFn[];
  return Validators.composeAsync(normalized) ?? undefined;
}


/**
 * Combines an array of initial values and validators into an `ExtendedFormArray` of individual `ExtendedFormGroup`s
 * and a single `ExtendedFormGroup` that contains all the controls.
 *
 * Note that the `formBuilder` argument is not normally needed because it is automatically injected.  However, if this is called from outside an object
 * construction phase, then formBuilder can be injected separately and passed in.
 */
export function buildFormGroupArray<ObjectType extends object>(
  initialValuesArray: ObjectType[], validators: ObjectValidators<ObjectType>, formBuilder?: ExtendedFormBuilder, destroyRef?: DestroyRef
): ExtendedFormGroupArray {
  const _formBuilder: ExtendedFormBuilder = formBuilder ?? inject(ExtendedFormBuilder);
  const rawGroups: ExtendedFormGroup[] = initialValuesArray.map(initialValues => buildFormGroup(initialValues, validators, _formBuilder));

  return {
    groups: _formBuilder.nonNullable.extendedArray(rawGroups),
    combinedGroup: combineFormGroups(rawGroups, _formBuilder, destroyRef)
  };
}

/**
 * Combines a list of `ExtendedFormGroup`s into a single `ExtendedFormGroup`.
 *
 * ASSUMPTION:  All control names are unique across the passed-in array.  If there are any duplicate names, the resulting `ExtendedFormGroup` will contain
 * one control for each duplicate name with the last such control encountered.
 *
 * Note that the `formBuilder` argument is not normally needed because it is automatically injected.  However, if this is called from outside an object
 * construction phase, then formBuilder can be injected separately and passed in.
 */
export function combineFormGroups(formGroups: ExtendedFormGroup[], formBuilder?: ExtendedFormBuilder, destroyRef?: DestroyRef): ExtendedFormGroup {
  const _formBuilder: ExtendedFormBuilder = formBuilder ?? inject(ExtendedFormBuilder);
  const _destroyRef: DestroyRef = destroyRef ?? inject(DestroyRef);

  const rawCombinedGroup: Record<string, AbstractControl> =
    formGroups.reduce(
      (accumulator, group) => {
        for (const [key, control] of Object.entries(group.extendedControls)) {
          accumulator[key] = control;
        }

        return accumulator;
      },
      {} as Record<string, AbstractControl>
    );

  const combinedGroup: ExtendedFormGroup = _formBuilder.nonNullable.extendedGroup(rawCombinedGroup);

  // Trigger initial validation so the combined group starts with the correct status.
  combinedGroup.updateValueAndValidity({emitEvent: false});

  // Whenever any child control’s status changes (including async validation), re-check the combined group’s validity so buttons/UI update immediately.
  Object.values(combinedGroup.extendedControls).forEach(control => {
    control.statusChanges
      .pipe(takeUntilDestroyed(_destroyRef))
      .subscribe(() => {
        combinedGroup.updateValueAndValidity({emitEvent: false});
      });
  });

  return combinedGroup;
}

// export function combineFormGroups(formGroups: ExtendedFormGroup[], formBuilder?: ExtendedFormBuilder): ExtendedFormGroup {
//   const _formBuilder: ExtendedFormBuilder = formBuilder ?? inject(ExtendedFormBuilder);
//
//   const rawCombinedGroup: Record<string, AbstractControl> =
//     formGroups.reduce(
//       (accumulator, group) => {
//         for (const [key, control] of Object.entries(group.extendedControls)) {
//           accumulator[key] = control;
//         }
//
//         return accumulator;
//       },
//       {} as Record<string, AbstractControl>
//     );
//
//   const combinedGroup = _formBuilder.nonNullable.extendedGroup(rawCombinedGroup);
//
//   // Force a validation pass so child sync/async validators initialize correctly.
//   combinedGroup.updateValueAndValidity({emitEvent: false});
//
//   return combinedGroup;
// }

// export function combineFormGroups(formGroups: ExtendedFormGroup[], formBuilder?: ExtendedFormBuilder): ExtendedFormGroup {
//   const _formBuilder: ExtendedFormBuilder = formBuilder ?? inject(ExtendedFormBuilder);
//
//   const rawCombinedGroup: Record<string, AbstractControl> =
//     formGroups.reduce(
//       (accumulator, group) => {
//         for (const [key, control] of Object.entries(group.extendedControls)) {
//           accumulator[key] = control;
//         }
//
//         return accumulator;
//       },
//       {} as Record<string, AbstractControl>
//     );
//
//   return _formBuilder.nonNullable.extendedGroup(rawCombinedGroup);
// }

/** Flattens a FormArray of FormGroups into an object with simple fieldName/value pairs, including values for disabled controls. */
export function flattenedFormValue<ObjectType extends object>(formArray: ExtendedFormArray<ExtendedFormGroup>): ObjectType {
  return formArray.extendedControls.reduce<ObjectType>(
    (accumulator, formGroup) => ({
      ...accumulator,
      ...formGroup.getRawValue() // Include disabled controls!
    }),
    {} as ObjectType
  );
}

/** Flattens a FormArray of FormGroups into an object with simple fieldName/value pairs, excluding values for disabled formGroups and formControls. */
export function flattenedEnabledFormValue<ObjectType extends object>(formArray: ExtendedFormArray<ExtendedFormGroup>): ObjectType {
  return formArray.extendedControls.reduce<ObjectType>(
    (accumulator, formGroup) => {
      return formGroup.enabled
        ? {
          ...accumulator,
          ...formGroup.value
        }
        : accumulator
    },
    {} as ObjectType
  );
}

export function hasChanged(control: ExtendedAbstractControl): boolean {
  if (hasNoValue(control)) return false;

  if (isExtendedFormGroup(control)) {
    // To head off any confusion, the "values" on the next line is to get the controls that are in the "values" of key/value pairs -- NOT form values.
    const childControls: ExtendedAbstractControl[] = Object.values(control.extendedControls);

    return control.initialLength !== childControls.length || childControls.some(hasChanged);
  }

  if (isExtendedFormArray(control)) {
    const currentUuids: string[] = control.extendedControls.map(childControl => childControl.uuid);

    // The array has changed if its UUIDS have changed (either quantity or order), or if any of its elements have changed.
    return !deepEqual(currentUuids, control.initialUuids) || control.extendedControls.some(hasChanged);
  }

  if (isExtendedFormControl(control)) {
    // There's no need to do the potentially expensive deepEqual() check if the control is pristine.
    return control.dirty && !deepEqual(control.initialValue, control.value);
  }

  throw new Error(`hasChanged() was somehow called with a non-extended control, even though this shouldn't be possible in our codebase.`);
}

export function isExtendedAbstractControl(control: AbstractControl): control is ExtendedAbstractControl {
  return 'uuid' in control; // uuid is required on all of our extended types.
}

export function isExtendedFormGroup(control: unknown): control is ExtendedFormGroup {
  return isFormGroup(control)
    && isExtendedAbstractControl(control)
    && 'initialLength' in control
    // eslint-disable-next-line no-restricted-syntax -- We're using the native control.controls to verify that the controls are extended.
    && Object.values(control.controls).every(isExtendedAbstractControl);
}

export function isExtendedFormArray(control: unknown): control is ExtendedFormArray {
  return isFormArray(control)
    && isExtendedAbstractControl(control)
    && 'initialUuids' in control
    // eslint-disable-next-line no-restricted-syntax -- We're using the native control.controls to verify that the controls are extended.
    && control.controls.every(isExtendedAbstractControl);
}

export function isExtendedFormControl(control: unknown): control is ExtendedFormControl {
  return isFormControl(control) && isExtendedAbstractControl(control) && 'initialValue' in control;
}

export function isFormControlState(thing: unknown): thing is FormControlState<unknown> {
  return isObject(thing) && 'value' in thing && 'disabled' in thing;
}

export type HasChangedFunction = typeof hasChanged;

export function setFormGroupValuesFromObject<ObjectType extends object>(formGroup: ExtendedFormGroup, valueObject: ObjectType): void {
  Object.keys(formGroup.extendedControls).forEach(fieldName => {
    if (fieldName in valueObject) {
      getExtendedControlFrom(formGroup, fieldName).patchValueAndMarkAsDirty(valueObject[fieldName]);
    }
  });
}

export function updateEnablementFor(control: AbstractControl | undefined, enableIf: boolean): void {
  if (hasNoValue(control)) return;

  if (enableIf) {
    control.enable();
  } else {
    control.disable();
  }
}

export function addOrRemoveValidatorsFor(control: AbstractControl | undefined, addIf: boolean, validators: OneOrArrayOf<ValidatorFn>): void {
  if (hasNoValue(control)) return;

  if (addIf) {
    control.addValidators(validators);
  } else {
    control.removeValidators(validators);
  }
}

export function updateEnablementAndValidatorsFor(control: AbstractControl, enableAndAddIf: boolean, validators: OneOrArrayOf<ValidatorFn>): void {
  updateEnablementFor(control, enableAndAddIf);
  addOrRemoveValidatorsFor(control, enableAndAddIf, validators);
}

/* eslint-disable @typescript-eslint/no-explicit-any -- We're extending Angular reactive forms types that use any. */
export function getExtendedControlFrom<
  TParent extends { [K in keyof TParent]: ExtendedAbstractControl<any, any> } = any,
  K extends keyof TParent = keyof TParent,
  TValue = TParent[K] extends ExtendedFormControl<infer V> ? V : any
>(
  formGroup: ExtendedFormGroup<TParent>,
  controlName: K
): ExtendedFormControl<TValue> {
  const control = formGroup.get(controlName);

  if (!isExtendedFormControl(control)) {
    throw new Error(
      `Expected ExtendedFormControl for '${String(controlName)}', but found ${
        hasValue(control) ? 'a different type of AbstractControl' : 'nothing'
      }`
    );
  }

  return control;
}

/* eslint-enable @typescript-eslint/no-explicit-any */

/* eslint-disable @typescript-eslint/no-explicit-any -- We're extending Angular reactive forms types that use any. */
export function getExtendedGroupFrom<
  TParent extends { [K in keyof TParent]: ExtendedAbstractControl<any, any> } = any,
  K extends keyof TParent = keyof TParent,
  G = TParent[K] extends ExtendedFormGroup<infer Inner> ? Inner : any
>(
  formGroup: ExtendedFormGroup<TParent>,
  controlName: K
): ExtendedFormGroup<G & { [K in keyof G]: ExtendedAbstractControl<any, any> }> {
  const control = formGroup.get(controlName);

  if (!isExtendedFormGroup(control)) {
    throw new Error(
      `Expected ExtendedFormGroup for '${String(controlName)}', but found ${hasValue(control) ? 'a different type of AbstractControl' : 'nothing'}`
    );
  }

  return control as unknown as ExtendedFormGroup<
    G & { [K in keyof G]: ExtendedAbstractControl<any, any> }
  >;
}

/* eslint-enable @typescript-eslint/no-explicit-any */

/* eslint-disable @typescript-eslint/no-explicit-any -- We're extending Angular reactive forms types that use any. */
export function getExtendedArrayFrom<
  TParent extends { [K in keyof TParent]: ExtendedAbstractControl<any, any> } = any,
  K extends keyof TParent = keyof TParent,
  I extends ExtendedAbstractControl<any, any> = TParent[K] extends ExtendedFormArray<infer U> ? U : ExtendedAbstractControl<any, any>
>(
  formGroup: ExtendedFormGroup<TParent>,
  controlName: K
): ExtendedFormArray<I> {
  const control = formGroup.get(controlName);

  if (!isExtendedFormArray(control)) {
    throw new Error(
      `Expected ExtendedFormArray for '${String(controlName)}', but found ${
        hasValue(control) ? 'a different type of AbstractControl' : 'nothing'
      }`
    );
  }

  return control as unknown as ExtendedFormArray<I>;
}

/* eslint-enable @typescript-eslint/no-explicit-any */

export function valueChangedFor<ValueType extends object>(watchedFieldName: keyof ValueType, previousValue: ValueType, newValue: ValueType): boolean {
  return !deepEqual(previousValue[watchedFieldName], newValue[watchedFieldName]);
}

export function valueChangedForOneOf<ValueType extends object>(watchedFieldNames: (keyof ValueType)[], previousValue: ValueType, newValue: ValueType): boolean {
  return watchedFieldNames.some(fieldName => valueChangedFor(fieldName, previousValue, newValue));
}

export function containsAnyValue(control: ExtendedFormControl): boolean {
  const value: unknown = control?.value;

  if (hasNoValue(value)) return false;
  if (isString(value)) return value.trim().length > 0;
  if (isArray(value)) return value.length > 0;
  if (isDate(value)) return !isNaN(value.getTime());
  if (isObject(value)) return Object.keys(value).length > 0;

  return true;
}

export function extractUniqueControlsFrom(...sources: Iterable<ExtendedFormControl | ControlRef>[]): ExtendedFormControl[] {
  const outputControls: Set<ExtendedFormControl> = new Set<ExtendedFormControl>();  // Gives us automatic deduplication in case some control sneaks in twice.

  for (const source of sources) {
    for (const item of source) {
      const control: ExtendedFormControl | undefined = isExtendedFormControl(item) ? item : item?.control;

      if (hasValue(control)) outputControls.add(control);
    }
  }

  return [...outputControls];
}

export function extractControlsFromControlRefs(queryList?: QueryList<ControlRef>): ExtendedFormControl[] {
  return isDefined(queryList) ? queryList.map(controlRef => controlRef.control) : [];
}

export function extractControlsFromFormRowRefs(queryList?: QueryList<FormRowRef>): ExtendedFormControl[] {
  // eslint-disable-next-line no-restricted-syntax -- FormRowRef has a controls property, which is not what we need to restrict.
  return isDefined(queryList) ? queryList.toArray().flatMap(formRowRef => formRowRef.controls) : [];
}
